import type { BotReach } from "@/entities/bot-stats"
import { plural } from "@/entities/audience"
import { SERIES_COLORS } from "@/shared/ui/charts/line-chart"
import { Skeleton } from "@/shared/ui/skeleton"

const number = new Intl.NumberFormat("ru-RU")
const NEUTRAL = "var(--color-muted-foreground)"

/**
 * Кому из аккаунтов в системе (всех, кто хоть раз заходил, не только текущих участников
 * канала) можно написать через ботов. Полоса делит всех на четыре
 * группы, у каждой подпись с числом: цвет только помогает найти часть на полосе.
 */
export function ReachPanel({ reach }: { reach: BotReach }) {
	const { system } = reach
	const unreachableInBots = Math.max(
		0,
		system.clubWithTelegram - system.clubNotInBots - system.clubReachable,
	)
	const parts = [
		{
			label: "Получат рассылку",
			hint: "Пишут хотя бы одному включённому боту и не заблокировали его",
			value: system.clubReachable,
			color: SERIES_COLORS[2],
		},
		{
			label: "Были в ботах, но не получат",
			hint: "Заблокировали бота или их бот выключен",
			value: unreachableInBots,
			color: SERIES_COLORS[1],
		},
		{
			label: "Не заходили ни в одного бота",
			hint: "Telegram привязан, но боту не писали: бот не может написать первым",
			value: system.clubNotInBots,
			color: SERIES_COLORS[0],
		},
		{
			label: "Без Telegram",
			hint: "Вошли по почте или через приложение и не привязали Telegram",
			value: system.clubWithoutTelegram,
			color: NEUTRAL,
		},
	]
	const total = Math.max(1, system.clubUsers)
	const outsiders = Math.max(0, system.reachablePeople - system.clubReachable)

	return (
		<section className="rounded-2xl border border-border bg-card p-5 sm:p-6">
			<p className="text-sm text-muted-foreground">Кому можно написать</p>
			<p className="mt-1 text-lg font-semibold tracking-tight">
				Рассылку через ботов получат{" "}
				<span className="tabular-nums">{number.format(system.clubReachable)}</span> из{" "}
				<span className="tabular-nums">{number.format(system.clubUsers)}</span>{" "}
				{plural(system.clubUsers, ["аккаунта", "аккаунтов", "аккаунтов"])} в системе
			</p>

			<div
				className="mt-4 flex h-3 w-full gap-0.5 overflow-hidden rounded-full"
				role="img"
				aria-label={parts.map((part) => `${part.label}: ${part.value}`).join(", ")}
			>
				{parts
					.filter((part) => part.value > 0)
					.map((part) => (
						<span
							key={part.label}
							title={`${part.label}: ${number.format(part.value)}`}
							className="h-full first:rounded-l-full last:rounded-r-full"
							style={{ width: `${(part.value / total) * 100}%`, background: part.color }}
						/>
					))}
			</div>

			<dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2">
				{parts.map((part) => (
					<div key={part.label} className="flex gap-2.5">
						<span
							aria-hidden
							className="mt-1.5 size-2.5 shrink-0 rounded-full"
							style={{ background: part.color }}
						/>
						<div className="min-w-0">
							<dt className="flex items-baseline gap-2 text-sm">
								<span>{part.label}</span>
								<span className="font-semibold tabular-nums">{number.format(part.value)}</span>
							</dt>
							<dd className="text-xs leading-snug text-muted-foreground">{part.hint}</dd>
						</div>
					</div>
				))}
			</dl>

			{outsiders > 0 ? (
				<p className="mt-4 border-t border-border pt-3 text-sm text-muted-foreground">
					Ещё{" "}
					<span className="font-medium text-foreground tabular-nums">
						{number.format(outsiders)}
					</span>{" "}
					{plural(outsiders, ["человек", "человека", "человек"])}{" "}
					{plural(outsiders, ["пишет", "пишут", "пишут"])} ботам, но аккаунта в системе у{" "}
					{plural(outsiders, ["него", "них", "них"])} нет. Им рассылка тоже придёт.
				</p>
			) : null}
		</section>
	)
}

/** Заглушка панели охвата: те же строки, полоса и четыре подписи, что у самой панели. */
export function ReachPanelSkeleton() {
	return (
		<section aria-hidden="true" className="rounded-2xl border border-border bg-card p-5 sm:p-6">
			<div className="flex h-5 items-center">
				<Skeleton className="h-3.5 w-32 rounded" />
			</div>
			<div className="mt-1 flex h-7 items-center">
				<Skeleton className="h-5 w-96 max-w-full rounded" />
			</div>
			<Skeleton className="mt-4 h-3 w-full rounded-full" />
			<div className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2">
				{[0, 1, 2, 3].map((index) => (
					<div key={index} className="flex gap-2.5">
						<Skeleton className="mt-1.5 size-2.5 shrink-0 rounded-full" />
						<div className="min-w-0 flex-1">
							<div className="flex h-5 items-center">
								<Skeleton className="h-3.5 w-44 rounded" />
							</div>
							<div className="flex h-[16.5px] items-center">
								<Skeleton className="h-3 w-64 max-w-full rounded" />
							</div>
						</div>
					</div>
				))}
			</div>
			<div className="mt-4 border-t border-border pt-3">
				<div className="flex h-5 items-center">
					<Skeleton className="h-3.5 w-full max-w-xl rounded" />
				</div>
			</div>
		</section>
	)
}
